import { ENV } from '../../config/env';
import { Customer } from '../../interfaces/customer.interface';
import {
  DOC_TYPE_AADE_CODE,
  DOC_TYPE_AADE_CODE_VALUES,
  DOC_TYPE_ALLOWED_AS_DELIVERY_NOTE,
  DOC_TYPE_DELIVERY_NOTE_DISABLED_BY_DEFAULT,
  UNPOSTABLE_AADE_DOC_TYPE_CODES,
} from '../../interfaces/documentType.interface';
import { InvoiceInterface } from '../../interfaces/invoice.interface';

export const isDocPostableUtil = (docType: DOC_TYPE_AADE_CODE_VALUES | undefined): boolean => {
  if (docType) {
    if (UNPOSTABLE_AADE_DOC_TYPE_CODES.includes(docType)) return false;
    return true;
  }

  return false;
};

export const isDocPostedToMyData = (doc: Partial<InvoiceInterface> | undefined) => {
  return !!doc?.mark;
};

export function shouldPrefixCountryCode(vatNumber: string): boolean {
  // Check if the first two characters are digits
  return /^\d{2}/.test(vatNumber);
}

export const getCustomerVatNumberForMyData = (customer: Customer | undefined) => {
  // The default vatNumber is covering person customers without VAT number
  let vatNumber = String(customer?.vatNumber || '');
  if (!vatNumber) return '';

  const customerCountryCode = customer?.country?.code;
  if (customerCountryCode && customerCountryCode !== 'GR' && shouldPrefixCountryCode(vatNumber)) {
    // Add 2-letter code country as prefix of vatNumber for non-Greek customers
    vatNumber = `${customerCountryCode}${vatNumber}`;
  }

  return vatNumber;
};

export const isQuantityReceiptForm = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES> | null) => {
  const types = [
    DOC_TYPE_AADE_CODE.QUANTITY_RECEIPT_FORM_RELATED,
    DOC_TYPE_AADE_CODE.QUANTITY_RECEIPT_FORM,
  ] as DOC_TYPE_AADE_CODE_VALUES[];

  return types.includes(docType as DOC_TYPE_AADE_CODE_VALUES);
};

export const isQuantityReceiptFormRelated = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES> | null) => {
  return docType === DOC_TYPE_AADE_CODE.QUANTITY_RECEIPT_FORM_RELATED;
};

const CORRELATION_REQUIRED_DOCS: Partial<DOC_TYPE_AADE_CODE_VALUES>[] = [
  DOC_TYPE_AADE_CODE.INVOICE_RETURN_RELATED,
  DOC_TYPE_AADE_CODE.DELIVERY_NOTE_RELATED,
  DOC_TYPE_AADE_CODE.QUANTITY_RECEIPT_FORM_RELATED,
  // A correlation is OPTIONAL for this doc type, not required - unlike the others in this list.
  // (Posting one, when present, goes through `multipleConnectedMarks` server-side, same as every
  // correlatable doc type except 9.1/10.1 - see `usesCorrelatedInvoicesField()` in billease-server's
  // `myDataGeneralUtils.ts` - but that's independent of whether the form should force the user to
  // add one.)
  // DOC_TYPE_AADE_CODE.RECEIPT_RETURN_RELATED,
];

export const isCorrelationRequired = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES>): boolean => {
  return CORRELATION_REQUIRED_DOCS.includes(docType);
};

export const isDocTypeDeliveryNote = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES> | null) => {
  const types = [
    DOC_TYPE_AADE_CODE.DELIVERY_NOTE_RELATED,
    DOC_TYPE_AADE_CODE.DELIVERY_NOTE_GROUP,
    DOC_TYPE_AADE_CODE.DELIVERY_NOTE,
    DOC_TYPE_AADE_CODE.INVERTED_DELIVERY_NOTE,
    DOC_TYPE_AADE_CODE.QUANTITY_RECEIPT_FORM_RELATED,
    DOC_TYPE_AADE_CODE.QUANTITY_RECEIPT_FORM,
  ] as DOC_TYPE_AADE_CODE_VALUES[];

  return types.includes(docType as DOC_TYPE_AADE_CODE_VALUES);
};

// A delivery note never consumes a posting credit (it's either not posted to AADE at all or
// posted for free, depending on plan), and a `PRO_FORMA_INVOICE` is never posted to AADE at all
// (`UNPOSTABLE_AADE_DOC_TYPE_CODES`) - so a low-remaining-credits warning is only relevant for
// every other doc type. Used by `DocForm.tsx`'s save action to decide whether to check credits.
export const isCreditConsumingDocType = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES> | null): boolean => {
  return !isDocTypeDeliveryNote(docType) && docType !== DOC_TYPE_AADE_CODE.PRO_FORMA_INVOICE;
};

// "Standard" delivery notes (Δελτίο Αποστολής) - 9.1/9.2/9.3/9.33 - as opposed to the Quantity
// Receipt Form variants (Δελτίο Ποσοτικής Παραλαβής, 10.1/10.2), which are still delivery-note
// doc types per `isDocTypeDeliveryNote()` above but a genuinely different myDATA document: a
// receiving/inbound-quantity record, not a full transit shipment. Used both for the "products to
// be weighed" toggle (`DocProducts.tsx`) and for NOR/WDTT visibility (`TransitToggles.tsx`) - two
// unrelated features that happen to share this exact doc-type set.
export const isDocTypeStandardDeliveryNote = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES> | null) => {
  const types = [
    DOC_TYPE_AADE_CODE.DELIVERY_NOTE_RELATED,
    DOC_TYPE_AADE_CODE.DELIVERY_NOTE_GROUP,
    DOC_TYPE_AADE_CODE.DELIVERY_NOTE,
    DOC_TYPE_AADE_CODE.INVERTED_DELIVERY_NOTE,
  ] as DOC_TYPE_AADE_CODE_VALUES[];

  return types.includes(docType as DOC_TYPE_AADE_CODE_VALUES);
};

export const isDocTypeInvertedDeliveryNote = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES> | null) => {
  return DOC_TYPE_AADE_CODE.INVERTED_DELIVERY_NOTE === docType;
};

export const getDocTypeAadeCodeToRender = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES> | null) => {
  if (docType === DOC_TYPE_AADE_CODE.INVERTED_DELIVERY_NOTE) return DOC_TYPE_AADE_CODE.DELIVERY_NOTE;

  return docType;
};

export const canBeMarkedAsDeliveryNote = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES>) => {
  return DOC_TYPE_ALLOWED_AS_DELIVERY_NOTE.includes(docType);
};

export const isDeliveryNoteDisabledByDefault = (docType: Partial<DOC_TYPE_AADE_CODE_VALUES>) => {
  return DOC_TYPE_DELIVERY_NOTE_DISABLED_BY_DEFAULT.includes(docType);
};

export const getAadeQrUrl = (doc: InvoiceInterface) => {
  if (doc?.aadeQr) return doc.aadeQr;

  if (ENV.DOMAIN && doc?.id) {
    return `${ENV.DOMAIN}share/doc/${doc.id}`;
  }

  return 'https://billease.gr';
};
